/*
  Dawn V2 : carrousel commun à toutes les sections.
  <v2-slider class="splide" data-splide='{"type":"loop","perPage":3}'> … </v2-slider>
  Les options sont celles de Splide (https://splidejs.com/guides/options/).
  Attributs en plus :
    data-mobile-only / data-desktop-only : le carrousel ne s'active que sur cette taille d'écran
    data-off : jamais de carrousel (les éléments restent posés en grille)
  Un carrousel ne démarre que lorsqu'il approche de l'écran (moins de travail au chargement de la page).
  Il envoie l'événement « v2-slider:mounted » une fois prêt.
*/
if (!customElements.get('v2-slider')) {
  customElements.define(
    'v2-slider',
    class V2Slider extends HTMLElement {
      connectedCallback() {
        this.mql = window.matchMedia('(min-width: 750px)');
        this.onChange = () => this.update();
        this.mql.addEventListener('change', this.onChange);
        this.update();
      }

      disconnectedCallback() {
        this.mql.removeEventListener('change', this.onChange);
        this.stopWatching();
        this.destroy();
      }

      shouldBeActive() {
        if (this.hasAttribute('data-off')) return false;
        if (this.hasAttribute('data-mobile-only')) return !this.mql.matches;
        if (this.hasAttribute('data-desktop-only')) return this.mql.matches;
        return true;
      }

      update() {
        if (typeof window.Splide === 'undefined') {
          window.addEventListener('load', () => this.update(), { once: true });
          return;
        }
        if (this.shouldBeActive()) {
          if (!this.splide) this.mountWhenNear();
        } else {
          this.destroy();
        }
      }

      // Démarre le carrousel quand il arrive à moins de 400 px de l'écran (tout de suite dans l'éditeur et le tiroir panier).
      mountWhenNear() {
        const eager = this.near || Shopify.designMode || !('IntersectionObserver' in window) || this.closest('cart-drawer, .drawer');
        if (eager) {
          this.near = true;
          this.mount();
          return;
        }
        if (this.nearObserver) return;
        this.nearObserver = new IntersectionObserver(
          (entries) => {
            if (!entries.some((entry) => entry.isIntersecting)) return;
            this.stopWatching();
            this.near = true;
            if (this.shouldBeActive() && !this.splide) this.mount();
          },
          { rootMargin: '400px 0px' }
        );
        this.nearObserver.observe(this);
      }

      stopWatching() {
        if (this.nearObserver) this.nearObserver.disconnect();
        this.nearObserver = null;
      }

      mount() {
        this.classList.remove('v2-slider--off');
        this.splide = new window.Splide(this);
        this.splide.on('active', (slide) => {
          this.querySelectorAll('video').forEach((video) => {
            if (!slide.slide.contains(video) && !video.muted) video.pause();
          });
        });
        const setTrackHeight = () => {
          const track = this.querySelector('.splide__track');
          if (track) this.style.setProperty('--track-height', `${track.offsetHeight}px`);
        };
        this.splide.on('mounted resized moved updated', setTrackHeight);
        this.splide.mount();
        setTrackHeight();
        this.dispatchEvent(new CustomEvent('v2-slider:mounted', { bubbles: true }));
        window.addEventListener('load', setTrackHeight, { once: true });
        if (document.fonts) document.fonts.ready.then(setTrackHeight);
        if ('ResizeObserver' in window) {
          this.trackObserver = new ResizeObserver(setTrackHeight);
          this.trackObserver.observe(this.querySelector('.splide__track'));
        }
        if (Shopify.designMode) {
          this.addEventListener('shopify:block:select', (event) => {
            const slide = event.target.closest('.splide__slide');
            if (slide && this.splide) this.splide.go(this.splide.Components.Slides.get().findIndex((s) => s.slide === slide));
          });
        }
      }

      destroy() {
        if (this.trackObserver) this.trackObserver.disconnect();
        if (this.splide) {
          this.splide.destroy(true);
          this.splide = null;
        }
        this.classList.add('v2-slider--off');
      }
    }
  );
}
